// External Imports
import { createSelector } from 'reselect';

// Internal Imports
import { isItemPreview } from '../utils/isItemPreview.js';
import { getActivePagination } from './getActivePagination.js';
import { getContentType, getMatch } from './getMatch.js';
import { getContentArchiveBase } from './getContentArchiveBase.js';
import { createGetContentItemBySlug } from './createGetContentItem.js';
import { getRelatedContentItemsByItem } from './relatedItemSelectors.js';

// Local Functions
const getActiveContentItemBySlug = createSelector([getContentType], (contentType) =>
  createGetContentItemBySlug(contentType),
);

const getActiveContentItem = createSelector(
  [
    getMatch,
    getActiveContentItemBySlug,
    // This sucks for memoization, but getItem for now is made in a way that doesn't get better than that
    (state) => state,
  ],
  ({ isArchive }, getItem, state) => (isArchive ? undefined : getItem(state)),
);

const getActiveContentItemRelatedItems = createSelector(
  [getActiveContentItem, (state) => state],
  (contentItem, state) => getRelatedContentItemsByItem(state, contentItem),
);

const hasFetchedActiveArchiveContent = createSelector(
  [getMatch, getActivePagination, getContentArchiveBase],
  ({ isArchive }, pagination, { pages = {} } = {}) =>
    isArchive ? !!pages[pagination.currentPage] : false,
);

const hasFetchedActiveContentItem = createSelector(
  [getActiveContentItem, getActiveContentItemRelatedItems],
  // Preview items don't have related items
  (contentItem, relatedItems) =>
    !!contentItem && (relatedItems.relatedPosts.length > 0 || isItemPreview(contentItem)),
);

const hasFetchedActiveContent = createSelector(
  [hasFetchedActiveArchiveContent, hasFetchedActiveContentItem],
  (hasFetchedArchive, hasFetchedItem) => hasFetchedArchive || hasFetchedItem,
);

// Module Exports
export { hasFetchedActiveContent };
